/**
 * The changes lens: what changed, field by field, every document compared with what its record was before it.
 * The documents of one run together, the runs newest first, grouped by day; a run shows its first three change rows
 * and "+ n more" opens the rest in place, and the lens shows forty runs before "earlier · n runs". A failed document
 * is a change that did not go through. Ported from components/data/History.tsx, re-skinned: no frames, timeline
 * dots in noun colours.
 */
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import type { History, HistoryEvent } from '../../lib/api.ts';
import { count, humanize } from '../../lib/format.ts';
import { stripSystem, systemName } from '../../lib/systems.ts';
import { Kicker, NounIcon, Pill, SystemBadge, cx, noun } from '../../design/primitives.tsx';
import { EmptyLens, NounCount, LensFrame, MorePill, PillTrack, isIdentifier, plural, type MessageRef } from './common.tsx';

const leaf = (p: string) => humanize(p.split('/').pop() ?? p);
/** A value that was there and is now something else. */
const changed = (e: HistoryEvent) => e.changes.filter((c) => c.from !== null);
/** An added field: one the record did not have before, brought by a later document (a shipment id once shipped). */
const added = (e: HistoryEvent) => e.changes.filter((c) => c.from === null);
const ADDED_SHOWN = 4;
/** Change rows a run shows before "+ n more". */
const ROWS = 3;
/** Runs the lens shows before "earlier · n runs". */
const GROUPS = 40;

/** The change rows of a document: its values that changed and its fields that were added; a bare document is one row. */
const rowsOf = (e: HistoryEvent) => (e.created ? 1 : Math.max(1, changed(e).length + added(e).length));

export function ChangesLens({ history, onMessage, onClose, every, onEvery }: {
  history: History; onMessage: (ref: MessageRef) => void; onClose?: () => void;
  /** "every document" instead of the changes, and its change: the page keeps it in the URL. */
  every?: boolean; onEvery?: (every: boolean) => void;
}) {
  const [allLocal, setAllLocal] = useState(false);
  const all = onEvery ? !!every : allLocal;
  const setAll = (v: boolean) => (onEvery ? onEvery(v) : setAllLocal(v));
  const [shown, setShown] = useState(GROUPS);
  const events = useMemo(() => history.events.filter((e) => all || (!e.created && e.changes.length > 0)), [history, all]);
  // The documents of one run together, in the order the run stored them; the runs newest first
  const runs = useMemo(() => {
    const by = new Map<string, HistoryEvent[]>();
    for (const e of events) {
      const id = e.executionId ?? e.message.uuid;
      by.set(id, [...(by.get(id) ?? []), e]);
    }
    return [...by.values()].map((es) => [...es].sort((a, b) => a.at - b.at));
  }, [events]);
  // The runs shown, by day
  const days = useMemo(() => {
    const out: [string, HistoryEvent[][]][] = [];
    for (const run of runs.slice(0, shown)) {
      const d = dayjs(run[run.length - 1].at).format('YYYY-MM-DD');
      if (out.length && out[out.length - 1][0] === d) out[out.length - 1][1].push(run);
      else out.push([d, [run]]);
    }
    return out;
  }, [runs, shown]);

  const changes = history.events.filter((e) => changed(e).length > 0);
  const fills = history.events.filter((e) => !e.created && changed(e).length === 0 && added(e).length > 0);
  const failedTries = changes.filter((e) => e.status === 'FAILED').length;
  const sentence = (
    <>
      {changes.length ? <>{count(changes.length)} {plural(changes.length, 'change')} to values</> : 'no value changed'}
      {fills.length > 0 && <> · fields added {count(fills.length)} {plural(fills.length, 'time')}</>}
      {failedTries > 0 && <> · <span className="text-fail">{count(failedTries)} did not go through</span></>}
      {' '}· from <NounCount kind="document" n={history.messages} />{history.truncated ? ', the newest' : ''}
    </>
  );
  const right = (
    <PillTrack>
      <Pill active={!all} onClick={() => setAll(false)}>changes</Pill>
      <Pill active={all} onClick={() => setAll(true)}>every document · {count(history.events.length)}</Pill>
    </PillTrack>
  );

  return (
    <LensFrame kind="document" title="what changed" count={sentence} right={right} onClose={onClose}>
      {events.length === 0 && <EmptyLens>Every document was the first of its record, and nothing changed after it.</EmptyLens>}
      <div className="flex flex-col gap-6">
        {days.map(([d, runs]) => (
          <section key={d} className="flex flex-col gap-3">
            <Kicker>{dayLabel(d)}</Kicker>
            {runs.map((run) => <RunGroup key={run[0].executionId ?? run[0].message.uuid} run={run} onMessage={onMessage} />)}
          </section>
        ))}
      </div>
      {runs.length > shown && <MorePill onClick={() => setShown((n) => n + GROUPS)}>earlier · {count(runs.length - shown)} {plural(runs.length - shown, 'run')}</MorePill>}
    </LensFrame>
  );
}

function dayLabel(d: string): string {
  const day = dayjs(d);
  if (day.isSame(dayjs(), 'day')) return 'today';
  if (day.isSame(dayjs().subtract(1, 'day'), 'day')) return 'yesterday';
  return day.format('dddd D.M.');
}

/** One run with what its documents did, in the order it stored them: the first ROWS change rows, the rest behind "+ n more". */
function RunGroup({ run, onMessage }: { run: HistoryEvent[]; onMessage: (ref: MessageRef) => void }) {
  const [more, setMore] = useState(false);
  const first = run[0];
  const last = run[run.length - 1];
  const failed = run.filter((e) => e.status === 'FAILED').length;
  const total = run.reduce((n, e) => n + rowsOf(e), 0);
  // Each document gets what is left of the budget; the ones after it are folded
  let budget = more ? Infinity : ROWS;
  const shown: { e: HistoryEvent; limit: number }[] = [];
  for (const e of run) {
    if (budget <= 0) break;
    shown.push({ e, limit: budget });
    budget -= rowsOf(e);
  }
  const hidden = more ? 0 : Math.max(0, total - ROWS);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 flex-wrap text-small text-n8">
        <NounIcon kind="run" size={14} />
        <span className="font-medium text-n9">{first.process ? stripSystem(first.process) : 'a document without a run'}</span>
        <span className="font-mono">{dayjs(first.at).format('HH:mm:ss')}{last.at > first.at ? `–${dayjs(last.at).format('HH:mm:ss')}` : ''}</span>
        <span>· {run.length} {plural(run.length, 'document')}</span>
        {failed > 0 && <span className="text-fail">· {failed} failed</span>}
      </div>
      <ol className="m-0 p-0 list-none flex flex-col">
        {shown.map(({ e, limit }, i) => (
          <Event key={`${e.message.uuid}${i}`} e={e} limit={Number.isFinite(limit) ? limit : undefined} last={i === shown.length - 1 && hidden === 0} onMessage={onMessage} />
        ))}
      </ol>
      {(hidden > 0 || more) && (
        <button type="button" onClick={() => setMore((m) => !m)}
          className="self-start ml-[86px] h-7 px-2.5 -mt-0.5 rounded-full bg-transparent border-0 cursor-pointer text-small text-n8 hover:text-n10 hover:bg-n3 transition-colors duration-[var(--t-micro)]">
          {more ? 'fewer' : `+ ${count(hidden)} more ${plural(hidden, 'change')}`}
        </button>
      )}
    </div>
  );
}

/** A value as it is set: identifiers and timestamps in mono. */
function Value({ v, className }: { v: string; className?: string }) {
  return <span className={cx(isIdentifier(v) && 'font-mono text-small', 'break-words', className)}>{v}</span>;
}

/** One document of a run; with a `limit` only that many of its change rows show (the run folds the rest). */
function Event({ e, limit, last, onMessage }: { e: HistoryEvent; limit?: number; last: boolean; onMessage: (ref: MessageRef) => void }) {
  const [more, setMore] = useState(false);
  const failed = e.status === 'FAILED';
  const all = changed(e);
  const values = limit === undefined ? all : all.slice(0, limit);
  const set = limit === undefined ? added(e) : added(e).slice(0, Math.max(0, limit - all.length));
  // The dot says what the document did: failed, changed values (record), brought new fields or a new record (document), nothing
  const dot = failed ? 'bg-fail' : values.length ? 'bg-rec' : set.length ? 'bg-doc' : 'bg-n5';
  return (
    <li className="grid grid-cols-[56px_16px_minmax(0,1fr)] gap-x-3">
      <span className="font-mono text-small text-n8 text-right pt-2.5">{dayjs(e.at).format('HH:mm')}</span>
      <span className="relative flex justify-center">
        {!last && <span className="absolute top-5 bottom-0 w-px bg-n4" aria-hidden="true" />}
        <span className={cx('relative mt-3 size-2 rounded-full', dot)} aria-hidden="true" />
      </span>
      <div className={cx('flex flex-col gap-2 py-2', !last && 'border-b border-n4')}>
        <div className="flex items-center gap-2 flex-wrap">
          <SystemBadge code={e.system} size={20} />
          <span className="text-body text-n10">
            {systemName(e.system)} {noun(e.cls)}{e.record && <span className="font-mono"> {e.record}</span>}
          </span>
          {e.created && <span className="text-micro uppercase tracking-[.08em] text-accent">new record</span>}
          {failed && <span className="text-micro uppercase tracking-[.08em] text-fail">failed · not applied</span>}
          <button type="button" onClick={() => onMessage(e.message)}
            className="ml-auto inline-flex items-center gap-1.5 text-small text-doc bg-transparent border-0 p-0 cursor-pointer hover:underline underline-offset-4">
            <NounIcon kind="document" size={14} />open document
          </button>
        </div>
        {values.length > 0 && (
          <div className="flex flex-col gap-1">
            {values.map((c) => (
              <div key={c.path} className="grid grid-cols-[minmax(0,160px)_minmax(0,1fr)] gap-4 items-baseline">
                <span className="text-small text-n7 truncate">{leaf(c.path)}</span>
                <span className="flex items-baseline gap-2 flex-wrap min-w-0 text-body">
                  <Value v={c.from ?? ''} className="text-n7 line-through decoration-1" />
                  <span className="text-n6" aria-hidden="true">→</span>
                  <Value v={c.to} className={cx('font-medium', failed ? 'text-fail' : 'text-n10')} />
                </span>
              </div>
            ))}
          </div>
        )}
        {failed && e.info && <span className="text-small text-fail">{e.info}</span>}
        {!failed && e.info && all.length === 0 && <span className="text-small text-n8">{e.info}</span>}
        {e.created && set.length > 0 && (
          <div className="text-small text-n8">
            <button type="button" onClick={() => setMore((m) => !m)} className="bg-transparent border-0 p-0 cursor-pointer text-n8 hover:text-n10">
              {more ? 'hide what it starts with' : `starts with ${set.length} ${plural(set.length, 'field')} · show`}
            </button>
            {more && (
              <div className="mt-2 grid grid-cols-[minmax(0,160px)_minmax(0,1fr)] gap-x-4 gap-y-1 mv-rise">
                {set.map((c) => (
                  <div key={c.path} className="contents">
                    <span className="text-small text-n7 truncate">{leaf(c.path)}</span>
                    <Value v={c.to} className="text-n9 truncate" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {set.length > 0 && !e.created && (
          <div className="flex flex-col gap-1">
            {(more ? set : set.slice(0, ADDED_SHOWN)).map((c) => (
              <div key={c.path} className="grid grid-cols-[minmax(0,160px)_minmax(0,1fr)] gap-4 items-baseline">
                <span className="text-small text-n7 truncate">{leaf(c.path)}</span>
                <span className="flex items-baseline gap-2 min-w-0 text-body">
                  <span className="text-micro uppercase tracking-[.08em] text-ok">added</span>
                  <Value v={c.to} className={cx('font-medium', failed ? 'text-fail' : 'text-n10')} />
                </span>
              </div>
            ))}
            {limit === undefined && set.length > ADDED_SHOWN && (
              <button type="button" onClick={() => setMore((m) => !m)} className="self-start text-small text-n8 bg-transparent border-0 p-0 cursor-pointer hover:text-n10">
                {more ? 'fewer' : `+ ${set.length - ADDED_SHOWN} more added fields`}
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
